import {
  AssessmentSectionSubjectVisibility,
  AssessmentType,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";

import AssessmentSectionSubjectVisibilityBanner from "../assessment/assessment-section-subject-visibility-banner";
import AssessmentLayout from "../layouts/assessment-layout";
import AssessmentQuestionItem from "../questions/assessment-question-item";
import {
  AssessmentAnswer,
  AssessmentAnswerWithResponderAndId,
  AssessmentQuestionContainer,
} from "../questions/types";
import { OverallGoalAssessmentQuestionDescriptionContext } from "./overall-goal-question-context";

const GoalBasedQuestions = ({
  assessmentType,
  individualGoalBasedQuestions,
  overallGoalBasedQuestion,
  proposedAnswers,
  formDisabled,
  showErrors,
  target,
  otherParticipantsAnswers,
  onUpdateAnswer,
  subjectVisibility,
}: {
  assessmentType: AssessmentType;
  target?: BasicUser | null;
  overallGoalBasedQuestion?: AssessmentQuestionContainer;
  individualGoalBasedQuestions: AssessmentQuestionContainer[];
  otherParticipantsAnswers?: AssessmentAnswerWithResponderAndId[];
  proposedAnswers: AssessmentAnswer[];
  formDisabled: boolean;
  showErrors: boolean;
  onUpdateAnswer: (answer: AssessmentAnswer) => void;
  subjectVisibility: AssessmentSectionSubjectVisibility;
}) => {
  const label = useLabel();
  const overallGoalBasedQuestionAnswer = proposedAnswers.find(
    (answer) =>
      overallGoalBasedQuestion &&
      answer.questionId === overallGoalBasedQuestion.id,
  );
  const sumAnswersOfIndividualGoalBasedQuestions =
    individualGoalBasedQuestions.reduce((sum, question) => {
      const answer = proposedAnswers.find(
        (answer) => answer.questionId === question.id,
      );
      if (!answer || !answer.integerAnswer) return sum;
      return sum + answer.integerAnswer;
    }, 0);
  const averageScoreOfIndividualGoalBasedQuestions =
    individualGoalBasedQuestions.length === 0 ||
    sumAnswersOfIndividualGoalBasedQuestions === 0
      ? 0
      : sumAnswersOfIndividualGoalBasedQuestions /
        individualGoalBasedQuestions.length;

  const currentUser = currentUserVar();
  return (
    <>
      <AssessmentLayout.SectionHeading>
        <AssessmentLayout.SectionHeadingTitle>
          {label("goal", { pluralize: true, capitalize: true })}
        </AssessmentLayout.SectionHeadingTitle>
        <AssessmentLayout.SectionHeadingDescription>
          Assess the {label("goal", { pluralize: true })} due in this{" "}
          {label("review")} cycle
        </AssessmentLayout.SectionHeadingDescription>
        {target && target?.id !== currentUser.id && (
          <AssessmentSectionSubjectVisibilityBanner
            subjectVisibility={subjectVisibility}
            className="mt-2 mb-4"
          />
        )}
      </AssessmentLayout.SectionHeading>

      <AssessmentLayout.SectionRegularQuestions>
        {individualGoalBasedQuestions.length === 0 && (
          <div className="p-6">
            <div className="text-sm text-gray-500">
              No {label("goal", { pluralize: true })} to assess.
            </div>
          </div>
        )}
        {individualGoalBasedQuestions.map((questionWithWeight) => {
          const { question } = questionWithWeight;
          const answer = proposedAnswers.find(
            (answer) => answer.questionId === question.id,
          );
          return (
            <AssessmentQuestionItem
              key={question.id}
              assessmentType={assessmentType}
              otherParticipantsAnswers={otherParticipantsAnswers?.filter(
                (answer) => answer.questionId === question.id,
              )}
              answer={answer}
              question={question}
              onUpdateAnswer={onUpdateAnswer}
              formDisabled={formDisabled}
              showError={showErrors}
              target={target}
            />
          );
        })}
        {overallGoalBasedQuestion && (
          <div>
            <OverallGoalAssessmentQuestionDescriptionContext.Provider
              value={{
                averageScoreOfIndividualGoalBasedQuestions: String(
                  averageScoreOfIndividualGoalBasedQuestions.toFixed(1),
                ).replace(".0", ""),
                scale:
                  overallGoalBasedQuestion.question.__typename ===
                  "OverallGoalAssessmentQuestionNode"
                    ? overallGoalBasedQuestion.question.labels.length
                    : undefined,
              }}
            >
              <AssessmentQuestionItem
                key={overallGoalBasedQuestion.id}
                assessmentType={assessmentType}
                otherParticipantsAnswers={otherParticipantsAnswers?.filter(
                  (answer) => answer.questionId === overallGoalBasedQuestion.id,
                )}
                answer={overallGoalBasedQuestionAnswer}
                question={overallGoalBasedQuestion.question}
                onUpdateAnswer={onUpdateAnswer}
                formDisabled={formDisabled}
                showError={showErrors}
                target={target}
              />
            </OverallGoalAssessmentQuestionDescriptionContext.Provider>
          </div>
        )}
      </AssessmentLayout.SectionRegularQuestions>
    </>
  );
};

export default GoalBasedQuestions;
